

import { useState, useRef, useCallback } from "react";
import { Upload, X } from "lucide-react";
import { getImageUrl } from "@/lib/utils";
import imageCompression from "browser-image-compression";

interface ImageUploadProps {
  images: string[];
  onImagesChange: (images: string[]) => void;
  maxImages?: number;
  compress?: boolean;
  uploadFn?: (file: File) => Promise<{ success: boolean; url?: string; message?: string }>;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export default function ImageUpload({
  images,
  onImagesChange,
  maxImages = 50,
  compress = true,
  uploadFn,
}: ImageUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressionInfo, setCompressionInfo] = useState<{ original: number; compressed: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const compressAndUpload = async (file: File) => {
    if (!uploadFn) return;

    setUploading(true);
    setCompressionInfo(null);

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    try {
      let fileToUpload = file;

      if (compress && file.type.startsWith("image/")) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 2048,
          useWebWorker: true,
          fileType: "image/webp",
          initialQuality: 0.85,
        };

        const compressedFile = await imageCompression(file, options);
        const originalName = file.name.replace(/\.[^/.]+$/, "");
        const webpFileName = `${originalName}.webp`;
        fileToUpload = new File([compressedFile], webpFileName, {
          type: "image/webp",
          lastModified: Date.now(),
        });

        setCompressionInfo({
          original: file.size,
          compressed: fileToUpload.size,
        });
      }

      const result = await uploadFn(fileToUpload);

      if (result.success && result.url) {
        onImagesChange([...images, result.url]);
        setPreviewUrl(null);
      } else {
        console.error("Upload failed:", result);
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      compressAndUpload(file);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (images.length >= maxImages) return;

      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        compressAndUpload(file);
      }
    },
    [images.length, maxImages, compressAndUpload]
  );

  const removeImage = (index: number) => {
    onImagesChange(images.filter((_, i) => i !== index));
  };

  const handleClick = () => {
    if (!uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div>
      {/* Drop Zone */}
      <div
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all mb-3 ${
          uploading
            ? "border-stone-300 bg-stone-50 cursor-not-allowed"
            : isDragging
            ? "border-amber-500 bg-amber-50"
            : "border-stone-300 hover:border-amber-400 hover:bg-amber-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />

        {uploading ? (
          <div className="space-y-3">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-20 h-20 mx-auto rounded-lg object-cover border border-stone-200"
              />
            )}
            <div className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin w-5 h-5 text-amber-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-sm text-stone-600">
                {compress ? "Compressing to WebP..." : "Uploading..."}
              </span>
            </div>
            {compressionInfo && (
              <div className="text-xs text-stone-600 space-y-1 bg-stone-100 rounded-lg p-2">
                <p>Original: {formatFileSize(compressionInfo.original)}</p>
                <p>Compressed: {formatFileSize(compressionInfo.compressed)}</p>
                <p className="text-emerald-600 font-medium">
                  Saved: {formatFileSize(compressionInfo.original - compressionInfo.compressed)} ({Math.round((1 - compressionInfo.compressed / compressionInfo.original) * 100)}%)
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2">
            <Upload className={`w-6 h-6 ${isDragging ? "text-amber-500" : "text-stone-400"}`} />
            <span className="text-sm text-stone-600">
              {isDragging ? "Drop image here" : "Click or drag to upload"}
            </span>
            <span className="text-xs text-stone-400">
              Supports: JPG, PNG, WebP (auto-compressed)
            </span>
          </div>
        )}
      </div>

      {/* Image Thumbnails */}
      {images.length > 0 && (
        <div className="grid grid-cols-5 gap-2">
          {images.map((img, idx) => (
            <div key={idx} className="relative group">
              <img
                src={getImageUrl(img)}
                alt={`Image ${idx + 1}`}
                className="w-full h-14 object-cover rounded-lg border border-stone-200 bg-stone-100"
                onError={(e) => {
                  console.error("Image failed to load:", getImageUrl(img));
                  (e.target as HTMLImageElement).src =
                    "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2E3YWYiIHN0cm9rZS13aWR0aD0iMiI+PHJlY3QgeD0iMyIgeT0iMyIgd2lkdGg9IjE4IiBoZWlnaHQ9IjE4IiByeD0iMiIgcnk9IjIiLz48Y2lyY2xlIGN4PSI4LjUiIGN5PSI4LjUiIHI9IjEuNSIvPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiLz48L3N2Zz4=";
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(idx);
                }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
